import {
  VEHICLE_CATEGORIES,
  VEHICLE_EXTRA_COLORS,
  VEHICLE_SKIP_KEYS,
} from './campaignDetailPage.constants';

export function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function fmtCurrency(amount?: number, currency = 'INR') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function fmtDateTime(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type VehicleRawItem = {
  type?: string;
  label?: string;
  name?: string;
  vehicleType?: string;
  count?: number;
};

export type VehicleLegendItem = {
  key: string;
  label: string;
  color: string;
  count: number;
};

function vehicleName(item: VehicleRawItem) {
  return item.type || item.label || item.name || item.vehicleType || '';
}

function toCount(value: unknown) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function normalizeVehicleKey(name: string) {
  let key = name.trim().toLowerCase().replace(/[\s_-]+/g, '');
  if (key.endsWith('count')) key = key.slice(0, -5);
  return key;
}

function formatVehicleLabel(key: string) {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function addVehicleCount(map: Map<string, number>, name: string, count: number) {
  const key = normalizeVehicleKey(name);
  if (!key || VEHICLE_SKIP_KEYS.has(key)) return;
  map.set(key, (map.get(key) || 0) + (Number.isFinite(count) ? count : 0));
}

function extractVehiclePayload(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const obj = raw as Record<string, unknown>;
  if (obj.vehicleDistribution && typeof obj.vehicleDistribution === 'object') {
    return obj.vehicleDistribution;
  }
  if (obj.vehicles && typeof obj.vehicles === 'object') return obj.vehicles;
  if (obj.data && typeof obj.data === 'object') return extractVehiclePayload(obj.data);
  return raw;
}

export function normalizeVehicleDistribution(raw: unknown): VehicleLegendItem[] {
  const counts = new Map<string, number>();
  const labels = new Map<string, string>();
  const payload = extractVehiclePayload(raw);

  if (Array.isArray(payload)) {
    for (const item of payload as VehicleRawItem[]) {
      const name = vehicleName(item);
      addVehicleCount(counts, name, toCount(item.count));
      const key = normalizeVehicleKey(name);
      if (key && name) labels.set(key, formatVehicleLabel(name));
    }
  } else if (payload && typeof payload === 'object') {
    for (const [name, value] of Object.entries(payload as Record<string, unknown>)) {
      const count =
        typeof value === 'object' && value && 'count' in value
          ? toCount((value as { count?: unknown }).count)
          : toCount(value);
      addVehicleCount(counts, name, count);
      const key = normalizeVehicleKey(name);
      if (key && !VEHICLE_SKIP_KEYS.has(key)) labels.set(key, formatVehicleLabel(name));
    }
  }

  const items: VehicleLegendItem[] = VEHICLE_CATEGORIES.map((cat) => ({
    ...cat,
    count: counts.get(cat.key) || 0,
  }));
  const seen = new Set(items.map((item) => item.key));

  let extraIndex = 0;
  for (const [key, count] of counts) {
    if (seen.has(key)) continue;
    items.push({
      key,
      label: labels.get(key) || formatVehicleLabel(key),
      color: VEHICLE_EXTRA_COLORS[extraIndex % VEHICLE_EXTRA_COLORS.length],
      count,
    });
    extraIndex += 1;
  }

  return items;
}
